import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

// Schema de la base de datos
interface OfflineDB extends DBSchema {
  'pending-visit-logs': {
    key: string; // visitId
    value: {
      visitId: string;
      userId: string;
      contactPerson: string;
      contactRole: string;
      agreements: string[];
      observations: string[];
      signatureDataURL: string; // base64 del canvas
      location: {
        lat: number;
        lng: number;
      } | null;
      timestamp: number;
      retries: number;
      status: 'pending' | 'syncing' | 'failed';
      error?: string;
    };
    indexes: { 'by-user': string; 'by-status': string };
  };
}

class OfflineDBService {
  private dbName = 'worksphere-offline';
  private version = 1;
  private db: IDBPDatabase<OfflineDB> | null = null;

  async init() {
    if (this.db) return this.db;

    this.db = await openDB<OfflineDB>(this.dbName, this.version, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('pending-visit-logs')) {
          const store = db.createObjectStore('pending-visit-logs', {
            keyPath: 'visitId',
          });
          store.createIndex('by-user', 'userId');
          store.createIndex('by-status', 'status');
        }
      },
    });

    return this.db;
  }

  // Guardar bitácora pendiente
  async savePendingLog(data: {
    visitId: string;
    userId: string;
    contactPerson: string;
    contactRole: string;
    agreements: string[];
    observations: string[];
    signatureDataURL: string;
    location: { lat: number; lng: number } | null;
  }) {
    const db = await this.init();

    await db.put('pending-visit-logs', {
      ...data,
      timestamp: Date.now(),
      retries: 0,
      status: 'pending',
    });

    // Disparar evento personalizado para actualizar UI
    window.dispatchEvent(new CustomEvent('offline-logs-updated'));
  }

  // Obtener todos los logs pendientes
  async getPendingLogs() {
    const db = await this.init();
    const pending = await db.getAllFromIndex('pending-visit-logs', 'by-status', 'pending');
    const failed = await db.getAllFromIndex('pending-visit-logs', 'by-status', 'failed');
    return [...pending, ...failed]; // ← Incluir ambos
  }

  // Obtener logs pendientes de un usuario
  async getPendingLogsByUser(userId: string) {
    const db = await this.init();
    const all = await db.getAllFromIndex('pending-visit-logs', 'by-user', userId);
    return all.filter(log => log.status === 'pending');
  }

  // contar logs pendientes
  async countPending() {
    const logs = await this.getPendingLogs();
    return logs.length;
  }

  // actualizar estado de un log
  async updateLogStatus(
    visitId: string,
    status: 'pending' | 'syncing' | 'failed',
    error?: string
  ) {
    const db = await this.init();
    const log = await db.get('pending-visit-logs', visitId);

    if (log) {
      log.status = status;
      if (error) log.error = error;
      if (status === 'syncing') log.retries += 1;
      await db.put('pending-visit-logs', log);
      window.dispatchEvent(new CustomEvent('offline-logs-updated'));
    }
  }

  // Eliminar log sincronizado
  async removeLog(visitId: string) {
    const db = await this.init();
    await db.delete('pending-visit-logs', visitId);
    window.dispatchEvent(new CustomEvent('offline-logs-updated'));
  }

  // obtener un log específico
  async getLog(visitId: string) {
    const db = await this.init();
    return await db.get('pending-visit-logs', visitId);
  }

  // limpiar todos los logs (usar con cuidado)
  async clearAll() {
    const db = await this.init();
    await db.clear('pending-visit-logs');
    window.dispatchEvent(new CustomEvent('offline-logs-updated'));
  }
}

export const offlineDB = new OfflineDBService();